/**
 * The theme choice while the app is open (spec docs/superpowers/specs/2026-09-27-app-ready-made-themes-design.md,
 * "Ruling 2", "Ruling 3" and "Ruling 6"). One module-level store, read with useSyncExternalStore:
 *
 *   - the app sidebar's scope enters it when an app page mounts and leaves it when the page goes;
 *     <html> carries the choice exactly while at least one app page is open;
 *   - someone who never chose has no choice (null): <html> carries nothing, as before themes existed;
 *   - the Theme menu (components/app-theme/app-theme-menu.tsx) reads it, previews through it and saves
 *     through it — the menu lives in the root layout's header, outside the app page's tree, which is
 *     why this is a store and not a React context;
 *   - the account's answer comes in through adoptAccountAppTheme, which refuses an answer that a save
 *     has overtaken.
 *
 * The cookie is the one place the current choice is read from on the client; setAppTheme writes it.
 */
import { DEFAULT_APP_THEME, isAppThemeChoice, type AppThemeChoice } from './app-themes'
import { readAppThemeCookie, writeAppThemeCookie } from './app-theme-cookie'
import { applyAppTheme, clearAppTheme, isAppThemeOffPath, markAppThemePreview } from './app-theme-dom'

export interface AppThemeSnapshot {
  /** Is an app page (one under the app sidebar) open? The menu offers the themes only then. */
  inAppShell: boolean
  /** The user's choice, as mirrored; null when they have never chosen one. */
  theme: AppThemeChoice | null
  /** What <html> shows: a choice being previewed, else the choice (null: never chosen). */
  shown: AppThemeChoice | null
}

/** What the server renders with: no app page, no choice. */
export const SERVER_APP_THEME_SNAPSHOT: AppThemeSnapshot = Object.freeze({
  inAppShell: false,
  theme: null,
  shown: null,
})

let scopes = 0
let fontClass: string | undefined
let theme: AppThemeChoice | null = null
let preview: AppThemeChoice | null = null
/** The look a preview stands in front of — the saved choice as the menu names it. */
let standing: AppThemeChoice | null = null
let snapshot: AppThemeSnapshot = SERVER_APP_THEME_SNAPSHOT
const listeners = new Set<() => void>()

let savesInFlight = 0
let epoch = 0

function emit() {
  const next: AppThemeSnapshot = { inAppShell: scopes > 0, theme, shown: preview ?? theme }
  if (next.inAppShell !== snapshot.inAppShell || next.theme !== snapshot.theme || next.shown !== snapshot.shown) {
    snapshot = next
    for (const listener of [...listeners]) listener()
  }
}

/** Make <html> match the store: the shown choice while an app page is open on an app path, else nothing. */
function paint() {
  if (typeof document === 'undefined') return
  const onAppPage = scopes > 0 && (typeof window === 'undefined' || !isAppThemeOffPath(window.location.pathname))
  const shown = preview ?? theme
  if (!onAppPage || shown === null) {
    clearAppTheme(fontClass)
    return
  }
  applyAppTheme(shown, fontClass)
  markAppThemePreview(preview ? standing : null)
}

const themeInCookie = (): AppThemeChoice | null => readAppThemeCookie()?.theme ?? null

export function subscribeAppTheme(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const getAppThemeSnapshot = (): AppThemeSnapshot => snapshot
export const getServerAppThemeSnapshot = (): AppThemeSnapshot => SERVER_APP_THEME_SNAPSHOT

/**
 * An app page opened: read the mirrored choice and paint it. Returns the function that closes it;
 * the last one closed takes the choice (and any preview) off <html>.
 */
export function enterAppShell(font?: string): () => void {
  scopes += 1
  if (font) fontClass = font
  if (scopes === 1) theme = themeInCookie()
  paint()
  emit()
  let open = true
  return () => {
    if (!open) return
    open = false
    scopes = Math.max(0, scopes - 1)
    if (scopes === 0) {
      preview = null
      standing = null
    }
    paint()
    emit()
  }
}

/**
 * The user chose `next` (null: no choice — the page as it was before themes): mirror it
 * (`<choice>.<owner>`, or no cookie) and paint it.
 */
export function setAppTheme(next: AppThemeChoice | null, owner: string | null | undefined): void {
  if (next !== null && !isAppThemeChoice(next)) return
  theme = next
  if (preview === next) {
    preview = null
    standing = null
  } else if (preview !== null && next !== null) {
    standing = next
  }
  writeAppThemeCookie(next, owner)
  paint()
  emit()
}

/**
 * Show a choice without saving it, in front of `saved` — the look the menu calls the person's own
 * (their choice, or for someone who never chose, Classic or Custom as their builder has it). Null, or
 * the look already there, ends the preview.
 */
export function previewAppTheme(next: AppThemeChoice | null, saved?: AppThemeChoice): void {
  const behind = saved ?? theme ?? DEFAULT_APP_THEME
  preview = next !== null && isAppThemeChoice(next) && next !== behind ? next : null
  standing = preview ? behind : null
  paint()
  emit()
}

/** Another tab may have saved a choice (no event crosses tabs): read the cookie again. */
export function refreshAppThemeFromCookie(): void {
  const mirrored = themeInCookie()
  if (mirrored === theme) return
  theme = mirrored
  paint()
  emit()
}

// ── Saves and the account's answer ───────────────────────────────────────────────────────────
//
// GET app-theme mirrors the account's choice into the cookie. An answer that was on its way while
// the user saved a new choice may carry the OLD one, and must not overwrite the new. So a save moves
// an epoch when it starts and when it ends; an answer is adopted only when no save was in flight
// when it was asked and the epoch has not moved since.

export function beginAppThemeSave(): void {
  savesInFlight += 1
  epoch += 1
}

export function endAppThemeSave(): void {
  savesInFlight = Math.max(0, savesInFlight - 1)
  epoch += 1
}

export const appThemeSaving = (): boolean => savesInFlight > 0
export const appThemeSaveEpoch = (): number => epoch

/**
 * The account says `answer` (asked at `askedAt`; null: the person never chose). Mirror and paint it
 * unless a save overtook it.
 */
export function adoptAccountAppTheme(answer: AppThemeChoice | null, owner: string | null | undefined, askedAt: number): boolean {
  if (savesInFlight > 0 || askedAt !== epoch) return false
  if (answer !== null && !isAppThemeChoice(answer)) return false
  setAppTheme(answer, owner)
  return true
}

/** Tests only: forget everything this module holds. */
export function resetAppThemeStoreForTests(): void {
  scopes = 0
  fontClass = undefined
  theme = null
  preview = null
  standing = null
  snapshot = SERVER_APP_THEME_SNAPSHOT
  listeners.clear()
  savesInFlight = 0
  epoch = 0
}
